import { posApi } from "@/api/pos-api";
import { ErrorResult } from "@/components/ui/ErrorResult";
import {
  ModalCUIngredient,
  ModalCUIngredientRef,
} from "@/components/ui/modals/ModalCUIngredient";
import { SectionPrivateHeader } from "@/components/ui/SectionPrivateHeader";
import { truncate } from "@/helpers/truncate";
import useTableFilters from "@/hooks/table/useTableFilters";
import { useDeleteIngredient } from "@/services/ingredients/mutation";
import { useIngredients } from "@/services/ingredients/queries";
import { Ingredient } from "@/types/ingredient";
import { Product } from "@/types/product";
import { DownloadOutlined, DeleteOutlined, EditOutlined, PlusSquareOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, message, Popconfirm, Space, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useAuth } from "@/auth/AuthProvider";
import { useRef } from "react";
import { ModalRestockIngredient, ModalRestockIngredientRef } from "@/components/ui/modals/ModalRestockIngredient";
import { useExportInventory } from "./inventory/useExportInventory";

export const InventoryPrivate = () => {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === "auditor";
  const modalCURef = useRef<ModalCUIngredientRef>(null);
  const modalRestockRef = useRef<ModalRestockIngredientRef>(null);

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: posApi.listCategories,
  });

  const [messageApi, contextHolder] = message.useMessage();

  const { tableParams, tableKey, resetFilters, handleTableChange } =
    useTableFilters();

  const { data, isLoading, isError, error, isFetching, refetch } =
    useIngredients(tableParams);

  const {
    contextHolder: exportContextHolder,
    generateExcel,
    loadingButtonExport,
  } = useExportInventory({ tableParams });

  const deleteIngredient = useDeleteIngredient();

  const onDeleteIngredient = async (id: Ingredient["id"]) => {
    deleteIngredient.mutate(id, {
      onSuccess: () => {
        messageApi.success(`El ingrediente se ha eliminado correctamente.`);
      },
      onError: () => {
        messageApi.error("Ups, algo salió mal. Intenta nuevamente.");
      },
    });
  };

  const baseColumns: ColumnsType<Ingredient> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (text: string) => truncate(text, 50),
    },
    {
      title: "Tipo unidad",
      dataIndex: "unit",
      key: "unit",
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
    },
  ];

  const actionColumn: ColumnsType<Ingredient>[number] = {
    title: "Acciones",
    dataIndex: "id",
    key: "id",
    width: 140,
    render: (id: Ingredient["id"], record: Ingredient) => (
      <Space size="middle">
        {/* REABASTECER */}
        <Tooltip placement="top" className="cursor-pointer" title="Aumentar stock">
          <button
            onClick={() => {
              modalRestockRef.current?.childFunction(record);
            }}
          >
            <PlusSquareOutlined className="text-lg text-green-500" />
          </button>
        </Tooltip>

        {/* EDIT */}
        <Tooltip placement="top" className="cursor-pointer" title="Editar">
          <button
            onClick={() => {
              modalCURef.current?.childFunction(record);
            }}
          >
            <EditOutlined className="text-lg" />
          </button>
        </Tooltip>

        {/* DELETE */}
        <Tooltip placement="top" className="cursor-pointer" title="Eliminar">
          <div>
            <Popconfirm
              title="¿Estás seguro/a que deseas eliminar a este ingrediente?"
              okText="Sí, Eliminar"
              cancelText="No, cancelar"
              cancelButtonProps={{
                style: { borderRadius: 12 },
              }}
              okButtonProps={{
                style: {
                  backgroundColor: "#ef4444",
                  border: 0,
                },
              }}
              placement="left"
              onConfirm={() => {
                onDeleteIngredient(id);
              }}
            >
              <button className="cursor-pointer">
                <DeleteOutlined className="text-lg" />
              </button>
            </Popconfirm>
          </div>
        </Tooltip>
      </Space>
    ),
  };

  const columns = isReadOnly ? baseColumns : [...baseColumns, actionColumn];

  if (isError) {
    return <ErrorResult error={error} refetch={refetch} />;
  }

  return (
    <>
      {contextHolder}
      {exportContextHolder}
      <SectionPrivateHeader
        title="Inventario de ingredientes"
        existsButton={!isReadOnly}
        onButtonClick={isReadOnly ? undefined : () => modalCURef.current?.childFunction()}
        extra={
          <Button
            type="default"
            icon={<DownloadOutlined />}
            loading={loadingButtonExport}
            onClick={() => generateExcel()}
            className="flex items-center gap-1 font-medium"
          >
            Descargar Excel
          </Button>
        }
      />

      <Table<Ingredient>
        columns={columns}
        dataSource={data?.items}
        key={tableKey}
        pagination={{
          current: tableParams?.pagination.current,
          pageSize: tableParams?.pagination.pageSize,
          total: data?.total,
        }}
        onChange={handleTableChange}
        loading={isLoading || isFetching}
        scroll={{ x: 1000 }}
      />

      <ModalCUIngredient ref={modalCURef} categories={categories || []} />

      <ModalRestockIngredient ref={modalRestockRef} />
    </>
  );
};
